import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { verifyEmail } from "@/services/auth";
import { PublicLegalFooter } from "@/components/PublicLegalFooter";

type State = "loading" | "success" | "error" | "missing-token";

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<State>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setState("missing-token");
      return;
    }
    let cancelled = false;
    verifyEmail(token)
      .then(() => {
        if (!cancelled) setState("success");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState("error");
        setErrorMessage(
          err instanceof Error && err.message
            ? err.message
            : "Bestätigung fehlgeschlagen. Der Link ist ungültig oder abgelaufen.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>QuizNudge</CardTitle>
          <CardDescription>E-Mail-Bestätigung</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          {state === "loading" && (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-muted-foreground" />
              <p>Deine E-Mail-Adresse wird bestätigt …</p>
            </>
          )}
          {state === "success" && (
            <>
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
              <h2 className="text-xl font-semibold">E-Mail bestätigt</h2>
              <p className="text-muted-foreground">
                Deine E-Mail-Adresse wurde erfolgreich bestätigt. Du kannst dich
                jetzt einloggen.
              </p>
              <Button asChild className="w-full">
                <Link to="/login?verified=1">Jetzt einloggen</Link>
              </Button>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="mx-auto h-12 w-12 text-destructive" />
              <h2 className="text-xl font-semibold">
                Bestätigung fehlgeschlagen
              </h2>
              <p className="text-muted-foreground">{errorMessage}</p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">Zur Anmeldung</Link>
              </Button>
            </>
          )}
          {state === "missing-token" && (
            <>
              <XCircle className="mx-auto h-12 w-12 text-destructive" />
              <h2 className="text-xl font-semibold">Token fehlt</h2>
              <p className="text-muted-foreground">
                Der Bestätigungslink ist ungültig. Bitte verwende den Link aus
                deiner Bestätigungs-E-Mail.
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">Zur Anmeldung</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      <PublicLegalFooter />
    </div>
  );
}
